import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { API_END_POINTS } from "../ApiEndPoints";

const initialState = {
  isLoading: false,
  isNotebookLoading: false, // Specific loading state for notebook fetch
  error: null,
  notebook: null,
  user: null,
  documents: [],
  chatHistory: [],
  notes: [],
  flashcardSets: [],
  audioSummaries: [],
  quizzes: [],
  mindMaps: [],
};

export const fetchStudentNotebook = createAsyncThunk(
  "studentNotebook/fetch",
  async ({ token, courseId, studentId, page = 1, limit = 50 }, { rejectWithValue }) => {
    try {
      let url;
      
      // If studentId is provided, use admin endpoint for viewing student notebook
      if (studentId) {
        url = `${API_END_POINTS.adminNotebookBase}/${studentId}/courses/${courseId}/notebook?page=${page}&limit=${limit}`;
      } else {
        url = `${API_END_POINTS.studentNotebookBase}/${courseId}/notebook?page=${page}&limit=${limit}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateNoteTitle = createAsyncThunk(
  "studentNotebook/updateNoteTitle",
  async ({ token, courseId, noteId, title }, { rejectWithValue }) => {
    try {
      const url = `${API_END_POINTS.studentNotebookBase}/${courseId}/notebook/notes/${noteId}`;
      const response = await axios.put(url, { title }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateFlashcardSetTitle = createAsyncThunk(
  "studentNotebook/updateFlashcardSetTitle",
  async ({ token, courseId, flashcardSetId, title }, { rejectWithValue }) => {
    try {
      const url = `${API_END_POINTS.studentNotebookBase}/${courseId}/notebook/flashcard-sets/${flashcardSetId}`;
      const response = await axios.put(url, { title }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateAudioSummaryTitle = createAsyncThunk(
  "studentNotebook/updateAudioSummaryTitle",
  async ({ token, courseId, audioSummaryId, title }, { rejectWithValue }) => {
    try {
      const url = `${API_END_POINTS.studentNotebookBase}/${courseId}/notebook/audio-summaries/${audioSummaryId}`;
      const response = await axios.put(url, { title }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateQuizTitle = createAsyncThunk(
  "studentNotebook/updateQuizTitle",
  async ({ token, courseId, quizId, title }, { rejectWithValue }) => {
    try {
      const url = `${API_END_POINTS.studentNotebookBase}/${courseId}/notebook/quizzes/${quizId}`;
      const response = await axios.put(url, { title }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateMindMapTitle = createAsyncThunk(
  "studentNotebook/updateMindMapTitle",
  async ({ token, courseId, mindMapId, title }, { rejectWithValue }) => {
    try {
      const url = `${API_END_POINTS.studentNotebookBase}/${courseId}/notebook/mindmaps/${mindMapId}`;
      const response = await axios.put(url, { title }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createNote = createAsyncThunk(
  "studentNotebook/createNote",
  async ({ token, courseId, title, content }, { rejectWithValue }) => {
    try {
      const url = `${API_END_POINTS.studentNotebookBase}/${courseId}/notebook/notes`;
      const response = await axios.post(url, { title, content }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchNote = createAsyncThunk(
  "studentNotebook/fetchNote",
  async ({ token, courseId, noteId, studentId }, { rejectWithValue }) => {
    try {
      let url;
      
      // If studentId is provided, use admin endpoint for viewing student note
      if (studentId) {
        url = `${API_END_POINTS.adminNotebookBase}/${studentId}/courses/${courseId}/notebook/notes/${noteId}`;
      } else {
        url = `${API_END_POINTS.studentNotebookBase}/${courseId}/notebook/notes/${noteId}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateNote = createAsyncThunk(
  "studentNotebook/updateNote",
  async ({ token, courseId, noteId, title, content }, { rejectWithValue }) => {
    try {
      const url = `${API_END_POINTS.studentNotebookBase}/${courseId}/notebook/notes/${noteId}`;
      const response = await axios.put(url, { title, content }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchFlashcards = createAsyncThunk(
  "studentNotebook/fetchFlashcards",
  async ({ token, courseId, flashcardSetId, studentId, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      let url;
      
      // If studentId is provided, use admin endpoint for viewing student flashcards
      if (studentId) {
        url = `${API_END_POINTS.adminNotebookBase}/${studentId}/courses/${courseId}/notebook/flashcard-sets/${flashcardSetId}/flashcards?page=${page}&limit=${limit}`;
      } else {
        url = `${API_END_POINTS.studentNotebookBase}/${courseId}/notebook/flashcard-sets/${flashcardSetId}/flashcards?page=${page}&limit=${limit}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const reviewFlashcard = createAsyncThunk(
  "studentNotebook/reviewFlashcard",
  async ({ token, courseId, flashcardId, studentId }, { rejectWithValue }) => {
    try {
      let url;
      
      // If studentId is provided, use admin endpoint for reviewing student flashcard
      if (studentId) {
        url = `${API_END_POINTS.adminNotebookBase}/${studentId}/courses/${courseId}/notebook/flashcards/${flashcardId}/review`;
      } else {
        url = `${API_END_POINTS.studentNotebookBase}/${courseId}/notebook/flashcards/${flashcardId}/review`;
      }
      
      const response = await axios.put(url, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchAudioSummary = createAsyncThunk(
  "studentNotebook/fetchAudioSummary",
  async ({ token, courseId, summaryId, studentId }, { rejectWithValue }) => {
    try {
      let url;
      
      // If studentId is provided, use admin endpoint for viewing student audio summary
      if (studentId) {
        url = `${API_END_POINTS.adminNotebookBase}/${studentId}/courses/${courseId}/notebook/audio-summaries/${summaryId}`;
      } else {
        url = `${API_END_POINTS.studentNotebookBase}/${courseId}/notebook/audio-summaries/${summaryId}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchQuiz = createAsyncThunk(
  "studentNotebook/fetchQuiz",
  async ({ token, courseId, quizId, studentId }, { rejectWithValue }) => {
    try {
      let url;
      
      // If studentId is provided, use admin endpoint for viewing student quiz
      if (studentId) {
        url = `${API_END_POINTS.adminNotebookBase}/${studentId}/courses/${courseId}/notebook/quizzes/${quizId}`;
      } else {
        url = `${API_END_POINTS.studentNotebookBase}/${courseId}/notebook/quizzes/${quizId}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchMindMap = createAsyncThunk(
  "studentNotebook/fetchMindMap",
  async ({ token, courseId, mindMapId, studentId }, { rejectWithValue }) => {
    try {
      let url;
      
      // If studentId is provided, use admin endpoint for viewing student mind map
      if (studentId) {
        url = `${API_END_POINTS.adminNotebookBase}/${studentId}/courses/${courseId}/notebook/mindmaps/${mindMapId}`;
      } else {
        url = `${API_END_POINTS.studentNotebookBase}/${courseId}/notebook/mindmaps/${mindMapId}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const submitQuizAnswer = createAsyncThunk(
  "studentNotebook/submitQuizAnswer",
  async ({ token, courseId, quizId, questionId, answerIndex }, { rejectWithValue }) => {
    try {
      const url = `${API_END_POINTS.studentNotebookBase}/${courseId}/notebook/quizzes/${quizId}/questions/${questionId}/answer`;
      const response = await axios.post(url, { answerIndex }, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const completeQuiz = createAsyncThunk(
  "studentNotebook/completeQuiz",
  async ({ token, courseId, quizId, studentId }, { rejectWithValue }) => {
    try {
      let url;
      
      // If studentId is provided, use admin endpoint for completing student quiz
      if (studentId) {
        url = `${API_END_POINTS.adminNotebookBase}/${studentId}/courses/${courseId}/notebook/quizzes/${quizId}/complete`;
      } else {
        url = `${API_END_POINTS.studentNotebookBase}/${courseId}/notebook/quizzes/${quizId}/complete`;
      }
      
      const response = await axios.post(url, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const sendChatMessage = createAsyncThunk(
  "studentNotebook/sendChatMessage",
  async ({ token, courseId, studentId, message, selectedDocuments = null }, { rejectWithValue }) => {
    try {
      const url = `${API_END_POINTS.studentNotebookBase}/${courseId}/notebook/chat`;
      
      const requestBody = { 
        message, 
        selectedDocuments 
      };
      
      // If studentId is provided (admin view), add it to the request body
      if (studentId) {
        requestBody.studentId = studentId;
      }
      
      const response = await axios.post(url, requestBody, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const generateAudioSummary = createAsyncThunk(
  "studentNotebook/generateAudioSummary",
  async ({ token, courseId, voiceStyle = "educational", language = "en", selectedDocuments = null }, { rejectWithValue }) => {
    try {
      const url = `${API_END_POINTS.studentNotebookBase}/${courseId}/notebook/audio-summaries/generate`;
      const response = await axios.post(url, { 
        voiceStyle, 
        language,
        selectedDocuments
      }, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const generateMindMap = createAsyncThunk(
  "studentNotebook/generateMindMap",
  async ({ token, courseId, sourceDocuments = [], title = null, difficulty = "medium", category = null, selectedDocuments = null }, { rejectWithValue }) => {
    try {
      const url = `${API_END_POINTS.studentNotebookBase}/${courseId}/notebook/mindmaps/generate`;
      const response = await axios.post(url, { 
        sourceDocuments,
        title,
        difficulty,
        category,
        selectedDocuments
      }, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const generateFlashcards = createAsyncThunk(
  "studentNotebook/generateFlashcards",
  async ({ token, courseId, sourceDocuments = [], count = 5, difficulty = "medium", category = null, selectedDocuments = null }, { rejectWithValue }) => {
    try {
      const url = `${API_END_POINTS.studentNotebookBase}/${courseId}/notebook/flashcards/generate`;
      const response = await axios.post(url, { 
        sourceDocuments,
        count,
        difficulty,
        category,
        selectedDocuments
      }, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const generateQuiz = createAsyncThunk(
  "studentNotebook/generateQuiz",
  async ({ token, courseId, sourceDocuments = [], difficulty = "medium", timeLimit = 30, title = null, selectedDocuments = null }, { rejectWithValue }) => {
    try {
      const url = `${API_END_POINTS.studentNotebookBase}/${courseId}/notebook/quizzes/generate`;
      const response = await axios.post(url, { 
        sourceDocuments,
        difficulty,
        timeLimit,
        title,
        selectedDocuments
      }, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const studentNotebookSlice = createSlice({
  name: "studentNotebook",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchStudentNotebook.pending, (state) => {
        state.isNotebookLoading = true;
        state.error = null;
      })
      .addCase(fetchStudentNotebook.fulfilled, (state, action) => {
        state.isNotebookLoading = false;
        state.error = null;
        const data = action.payload?.data || action.payload;
        state.notebook = data?.notebook || null;
        state.user = data?.user || null;
        state.documents = data?.documents || [];
        state.chatHistory = data?.chatHistory || [];
        state.notes = data?.notes || [];
        state.flashcardSets = data?.flashcardSets || [];
        state.audioSummaries = data?.audioSummaries || [];
        state.quizzes = data?.quizzes || [];
        state.mindMaps = data?.mindMaps || [];
      })
      .addCase(fetchStudentNotebook.rejected, (state, action) => {
        state.isNotebookLoading = false;
        state.error = action.payload || "Failed to load notebook";
      })
      // Update cases
      .addCase(updateNoteTitle.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(updateNoteTitle.fulfilled, (state, action) => { 
        state.isLoading = false; 
        // Update the note title in the Redux state
        if (action.payload.data) {
          const updatedNote = action.payload.data;
          const noteIndex = state.notes.findIndex(note => note.id === updatedNote.id);
          if (noteIndex !== -1) {
            state.notes[noteIndex] = updatedNote;
            console.log('📝 Updated note title in Redux state:', updatedNote.title);
          }
        }
      })
      .addCase(updateNoteTitle.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; })
      .addCase(updateFlashcardSetTitle.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(updateFlashcardSetTitle.fulfilled, (state, action) => { 
        state.isLoading = false; 
        // Update the flashcard set title in the Redux state
        if (action.payload.data && action.payload.data.flashcardSet) {
          const setId = action.payload.data.flashcardSet.id;
          const updatedTitle = action.payload.data.flashcardSet.title;
          const setIndex = state.flashcardSets.findIndex(set => set.id === setId);
          if (setIndex !== -1) {
            state.flashcardSets[setIndex].title = updatedTitle;
            console.log('🃏 Updated flashcard set title in Redux state:', updatedTitle);
          }
        }
      })
      .addCase(updateFlashcardSetTitle.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; })
      .addCase(updateAudioSummaryTitle.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(updateAudioSummaryTitle.fulfilled, (state, action) => { 
        state.isLoading = false; 
        // Update the audio summary title in the Redux state
        if (action.payload.data && action.payload.data.audioSummary) {
          const summaryId = action.payload.data.audioSummary.id;
          const updatedTitle = action.payload.data.audioSummary.title;
          const summaryIndex = state.audioSummaries.findIndex(summary => summary.id === summaryId);
          if (summaryIndex !== -1) {
            state.audioSummaries[summaryIndex].title = updatedTitle;
            console.log('🎵 Updated audio summary title in Redux state:', updatedTitle);
          }
        }
      })
      .addCase(updateAudioSummaryTitle.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; })
      .addCase(updateQuizTitle.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(updateQuizTitle.fulfilled, (state, action) => { 
        state.isLoading = false; 
        // Update the quiz title in the Redux state
        if (action.payload.data && action.payload.data.quiz) {
          const quizId = action.payload.data.quiz.id;
          const updatedTitle = action.payload.data.quiz.title;
          const quizIndex = state.quizzes.findIndex(quiz => quiz.id === quizId);
          if (quizIndex !== -1) {
            state.quizzes[quizIndex].title = updatedTitle;
            console.log('❓ Updated quiz title in Redux state:', updatedTitle);
          }
        }
      })
      .addCase(updateQuizTitle.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; })
      .addCase(updateMindMapTitle.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(updateMindMapTitle.fulfilled, (state, action) => { 
        state.isLoading = false; 
        // Update the mind map title in the Redux state
        if (action.payload.data && action.payload.data.mindMap) {
          const mindMapId = action.payload.data.mindMap.id;
          const updatedTitle = action.payload.data.mindMap.title;
          const mindMapIndex = state.mindMaps.findIndex(mindMap => mindMap.id === mindMapId);
          if (mindMapIndex !== -1) {
            state.mindMaps[mindMapIndex].title = updatedTitle;
            console.log('🗺️ Updated mind map title in Redux state:', updatedTitle);
          }
        }
      })
      .addCase(updateMindMapTitle.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; })
      .addCase(createNote.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(createNote.fulfilled, (state, action) => { 
        state.isLoading = false; 
        // Add the new note to the list
        if (action.payload.data) {
          console.log('📝 Adding note to Redux state:', action.payload.data);
          state.notes.unshift(action.payload.data);
          console.log('📝 Notes after adding:', state.notes.length);
        }
      })
      .addCase(createNote.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; })
      .addCase(fetchNote.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchNote.fulfilled, (state) => { state.isLoading = false; })
      .addCase(fetchNote.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; })
      .addCase(updateNote.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(updateNote.fulfilled, (state, action) => {
        state.isLoading = false;
        // Update the note in the Redux state
        if (action.payload.data) {
          const updatedNote = action.payload.data;
          const noteIndex = state.notes.findIndex(note => note.id === updatedNote.id);
          if (noteIndex !== -1) {
            state.notes[noteIndex] = updatedNote;
            console.log('📝 Updated note in Redux state:', updatedNote.id);
          }
        }
      })
      .addCase(updateNote.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; })
      .addCase(fetchFlashcards.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchFlashcards.fulfilled, (state) => { state.isLoading = false; })
      .addCase(fetchFlashcards.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; })
      .addCase(reviewFlashcard.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(reviewFlashcard.fulfilled, (state) => { state.isLoading = false; })
      .addCase(reviewFlashcard.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; })
      .addCase(fetchAudioSummary.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchAudioSummary.fulfilled, (state) => { state.isLoading = false; })
      .addCase(fetchAudioSummary.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; })
      .addCase(fetchQuiz.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchQuiz.fulfilled, (state) => { state.isLoading = false; })
      .addCase(fetchQuiz.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; })
      .addCase(fetchMindMap.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchMindMap.fulfilled, (state) => { state.isLoading = false; })
      .addCase(fetchMindMap.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; })
      .addCase(submitQuizAnswer.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(submitQuizAnswer.fulfilled, (state) => { state.isLoading = false; })
      .addCase(submitQuizAnswer.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; })
      .addCase(completeQuiz.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(completeQuiz.fulfilled, (state) => { state.isLoading = false; })
      .addCase(completeQuiz.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; })
      .addCase(sendChatMessage.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(sendChatMessage.fulfilled, (state, action) => { 
        state.isLoading = false; 
        // Don't automatically add messages - let component handle this
        // This allows for better control over message flow
      })
      .addCase(sendChatMessage.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; })
      .addCase(generateAudioSummary.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(generateAudioSummary.fulfilled, (state, action) => { 
        state.isLoading = false; 
        // Add the new audio summary to the list
        if (action.payload.data && action.payload.data.audioSummary) {
          console.log('🎵 Adding audio summary to Redux state:', action.payload.data.audioSummary);
          state.audioSummaries.unshift(action.payload.data.audioSummary);
          console.log('🎵 Audio summaries after adding:', state.audioSummaries.length);
        }
      })
      .addCase(generateAudioSummary.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; })
      .addCase(generateMindMap.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(generateMindMap.fulfilled, (state, action) => { 
        state.isLoading = false; 
        // Add the new mind map to the list
        if (action.payload.data && action.payload.data.mindMap) {
          console.log('🗺️ Adding mind map to Redux state:', action.payload.data.mindMap);
          state.mindMaps.unshift(action.payload.data.mindMap);
          console.log('🗺️ Mind maps after adding:', state.mindMaps.length);
        }
      })
      .addCase(generateMindMap.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; })
      .addCase(generateFlashcards.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(generateFlashcards.fulfilled, (state, action) => { 
        state.isLoading = false; 
        // Add the new flashcard set to the list
        if (action.payload.data && action.payload.data.flashcardSet) {
          console.log('🃏 Adding flashcard set to Redux state:', action.payload.data.flashcardSet);
          state.flashcardSets.unshift(action.payload.data.flashcardSet);
          console.log('🃏 Flashcard sets after adding:', state.flashcardSets.length);
        }
      })
      .addCase(generateFlashcards.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; })
      .addCase(generateQuiz.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(generateQuiz.fulfilled, (state, action) => { 
        state.isLoading = false; 
        // Add the new quiz to the list
        if (action.payload.data && action.payload.data.quiz) {
          console.log('❓ Adding quiz to Redux state:', action.payload.data.quiz);
          const newQuiz = { ...action.payload.data.quiz };
          // Ensure a timestamp exists so UI sorts new item to top immediately
          const nowIso = new Date().toISOString();
          if (!newQuiz.createdAt && !newQuiz.created_at) {
            newQuiz.createdAt = nowIso;
          }
          state.quizzes.unshift(newQuiz);
          // Keep quizzes sorted by newest first using available timestamp fields
          state.quizzes.sort((a, b) => {
            const aTime = new Date(a.createdAt || a.created_at || a.updatedAt || a.updated_at || nowIso).getTime();
            const bTime = new Date(b.createdAt || b.created_at || b.updatedAt || b.updated_at || nowIso).getTime();
            return bTime - aTime;
          });
          console.log('❓ Quizzes after adding:', state.quizzes.length);
        }
      })
      .addCase(generateQuiz.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; });
  },
});

export default studentNotebookSlice.reducer;